const PERF_SESSION_STORAGE_KEY = "hr_perf_session_id";
const PERF_NAV_TS_KEY = "hr_perf_nav_ts";
const PERF_MONITOR_FORCE_OFF = true;

function isPerfEnabled() {
  if (PERF_MONITOR_FORCE_OFF) {
    return false;
  }
  if (typeof process !== "undefined" && process.env.NODE_ENV !== "development") {
    return false;
  }
  if (typeof window === "undefined") {
    return false;
  }
  return window.__PERF_ENABLED__ === true;
}

function generateId(prefix = "id") {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now().toString(36)}_${rand}`;
}

function safeNow() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function readOrCreateSessionId() {
  try {
    const existing = sessionStorage.getItem(PERF_SESSION_STORAGE_KEY);
    if (existing) return existing;
    const created = generateId("sess");
    sessionStorage.setItem(PERF_SESSION_STORAGE_KEY, created);
    return created;
  } catch {
    return generateId("sess");
  }
}

function getGlobalPerfState() {
  if (typeof window === "undefined") {
    return {
      sessionId: generateId("sess"),
      currentPage: null,
      apiLastSeenByKey: {},
      firstPageSeenAt: Date.now(),
      appStartedAtMs: safeNow(),
    };
  }
  if (!window.__hrPerfState) {
    window.__hrPerfState = {
      sessionId: readOrCreateSessionId(),
      currentPage: null,
      apiLastSeenByKey: {},
      firstPageSeenAt: Date.now(),
      appStartedAtMs: safeNow(),
    };
  }
  return window.__hrPerfState;
}

function safeStringifyByteSize(value) {
  try {
    return new Blob([JSON.stringify(value ?? null)]).size;
  } catch {
    try {
      return String(value ?? "").length;
    } catch {
      return 0;
    }
  }
}

function logPerfFe(message, fields = {}) {
  if (!isPerfEnabled()) return;
  const payload = {
    ts: new Date().toISOString(),
    ...fields,
  };
  console.info(`[PERF_FE] ${message}`, payload);
}

function markNavigationStart(pathname) {
  if (!isPerfEnabled()) return null;
  const state = getGlobalPerfState();
  const pageId = generateId("page");
  const navStartMs = safeNow();
  state.currentPage = {
    id: pageId,
    path: pathname || "/",
    navStartMs,
    firstApiSeen: false,
    apiCount: 0,
  };
  try {
    sessionStorage.setItem(PERF_NAV_TS_KEY, String(Date.now()));
  } catch {
    // ignore
  }
  logPerfFe("PAGE_NAV_START", {
    session_id: state.sessionId,
    page_id: pageId,
    path: state.currentPage.path,
  });
  return state.currentPage;
}

function markSignificantRender(pathname) {
  if (!isPerfEnabled()) return;
  const state = getGlobalPerfState();
  const page = state.currentPage;
  if (!page) return;
  const renderMs = Math.max(0, safeNow() - page.navStartMs);
  logPerfFe("PAGE_RENDER_SIGNIFICANT", {
    session_id: state.sessionId,
    page_id: page.id,
    path: pathname || page.path,
    render_ms: Number(renderMs.toFixed(2)),
  });
}

function markBundleLoad() {
  if (!isPerfEnabled()) return;
  const state = getGlobalPerfState();
  const navEntry =
    typeof performance !== "undefined" &&
    typeof performance.getEntriesByType === "function"
      ? performance.getEntriesByType("navigation")?.[0]
      : null;
  const scriptEntries =
    typeof performance !== "undefined" &&
    typeof performance.getEntriesByType === "function"
      ? performance.getEntriesByType("resource")?.filter((e) => e.initiatorType === "script") || []
      : [];

  const scriptTransferBytes = scriptEntries.reduce(
    (sum, e) => sum + (Number(e.transferSize) || 0),
    0
  );
  const scriptDurationMs = scriptEntries.reduce((sum, e) => sum + (Number(e.duration) || 0), 0);

  logPerfFe("BUNDLE_LOAD", {
    session_id: state.sessionId,
    navigation_dom_content_loaded_ms: Number(navEntry?.domContentLoadedEventEnd || 0),
    navigation_load_event_end_ms: Number(navEntry?.loadEventEnd || 0),
    script_count: scriptEntries.length,
    script_transfer_bytes: scriptTransferBytes,
    script_duration_ms: Number(scriptDurationMs.toFixed(2)),
    strict_mode_enabled: false,
  });
}

function onApiStart({ method, url, requestId }) {
  if (!isPerfEnabled()) return;
  const state = getGlobalPerfState();
  const page = state.currentPage;
  const key = `${String(method || "GET").toUpperCase()} ${url || ""}`;
  const now = Date.now();
  const prev = state.apiLastSeenByKey[key];
  state.apiLastSeenByKey[key] = now;
  const duplicateWindowMs = prev ? now - prev : null;

  if (page) {
    page.apiCount += 1;
    if (!page.firstApiSeen) {
      page.firstApiSeen = true;
      const firstApiMs = Math.max(0, safeNow() - page.navStartMs);
      logPerfFe("TIME_TO_FIRST_API", {
        session_id: state.sessionId,
        page_id: page.id,
        path: page.path,
        request_id: requestId,
        first_api_ms: Number(firstApiMs.toFixed(2)),
      });
    }
  }

  logPerfFe("API_START", {
    session_id: state.sessionId,
    page_id: page?.id || null,
    path: page?.path || null,
    request_id: requestId,
    method: String(method || "GET").toUpperCase(),
    url,
    suspected_duplicate: Boolean(duplicateWindowMs != null && duplicateWindowMs < 1200),
    duplicate_delta_ms: duplicateWindowMs,
  });
}

function onApiEnd({ method, url, requestId, status, durationMs, data }) {
  if (!isPerfEnabled()) return;
  const state = getGlobalPerfState();
  const page = state.currentPage;
  logPerfFe("API_END", {
    session_id: state.sessionId,
    page_id: page?.id || null,
    path: page?.path || null,
    request_id: requestId,
    method: String(method || "GET").toUpperCase(),
    url,
    status: Number(status) || 0,
    duration_ms: Number((durationMs || 0).toFixed(2)),
    response_size_bytes: safeStringifyByteSize(data),
  });
}

export {
  generateId,
  getGlobalPerfState,
  logPerfFe,
  markBundleLoad,
  markNavigationStart,
  markSignificantRender,
  onApiStart,
  onApiEnd,
  isPerfEnabled,
};
